import { FC, useState, useMemo, ChangeEvent, useEffect } from 'react';
import { Column, useTable } from 'react-table';
import AdminLeagueService from '../../services/admin/adminLeague'
import HelperService from '../../services/helper'
import useNotyf from '../../context/useNotify';
import { League, UpdateLeagueDTO, CreateLeagueDTO } from '../../models/league';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrashAlt, faChartBar, faFlagCheckered, faPlay} from "@fortawesome/free-solid-svg-icons";
import ReactPaginate from 'react-paginate';
import { Meta, QueryParams } from '../../models/misc';
import { StyledInstructions, StyledPagination, StyledTable } from './Styles';
import ReactModal from '../common/ReactModal';
import LeagueService from "../../services/league"
import StartLeagueModal from './sections/StartLeagueModal';
import ConfirmationModalContent from '../common/ConfirmationModalContent';
import instructions from "../../content-options/admin/instructions"

const LeagueTable: FC = () => {
  const notyf = useNotyf()

  // Data
  const [leagues, setLeagues] = useState<League[]>([]);
  
  // API
    const fetchLeagues = async (page: number = 1) => {
    try {
      const filters: QueryParams = { 
        page: page,
        pageSize: meta.pageSize 
      };
      const data = await LeagueService.listLeagues(filters);
      setLeagues(data.data);
      setMeta(data.meta);
    } catch (err) {
      console.log("Error fetching leagues: ", err);
      HelperService.showNotfyErr(notyf, err, "Error fetching leagues.");
    }
    };
  
    const updateLeague = async (id: string, updateLeague: League) => {
        try {
            const updateDto: UpdateLeagueDTO = {
                leagueName: updateLeague.leagueName,
                continent: updateLeague.continent,
                division: updateLeague.division,
                console: updateLeague.console,
                status: updateLeague.status,
                startDate: updateLeague.startDate,
                rounds: updateLeague.rounds,
                dates: updateLeague.dates,
                times: updateLeague.times,
              };              
        await AdminLeagueService.updateLeague(id, updateDto);
        notyf.success("League updated.")
      } catch (err) {
        console.log("Error updating league: ", err);
        HelperService.showNotfyErr(notyf, err, "Error updating league.");
      }
    };
  
    const getLeague = async (id: string) => {
      try {
        const league = await LeagueService.getLeagueById(id);
        return league
      } catch (err) {
        console.log("Error updating fixture: ", err);
        HelperService.showNotfyErr(notyf, err, "Error updating fixture.");
      }
    };

    const createLeague = async (dto: CreateLeagueDTO) => {
        try {
          await AdminLeagueService.createLeague(dto);
          notyf.success("League created.")
        } catch (err) {
          console.log("Error creating league: ", err);
          HelperService.showNotfyErr(notyf, err, "Error creating league.");
        }
    };
  
    const deleteLeague = async (id: string) => {
      try {
        await AdminLeagueService.deleteLeague(id);
        notyf.success("League deleted.")
      } catch (err) {
        console.log("Error deleting league: ", err);
        HelperService.showNotfyErr(notyf, err, "Error deleting league.");
      }
    };

    const updateLeagueStats = async (id: string) => {
      try {
        await AdminLeagueService.updateLeagueStats(id);
        notyf.success("League stats updated.")
      } catch (err) {
        console.log("Error updating league stats: ", err);
        HelperService.showNotfyErr(notyf, err, "Error updating league stats.");
      }
    }
  
    const endLeague = async (id: string) => {
      try {
        await AdminLeagueService.endLeague(id);
        notyf.success("League ended.")
      } catch (err) {
        console.log("Error ending league: ", err);
        HelperService.showNotfyErr(notyf, err, "Error ending league.");
      }
    }
  
    // Methods

  // Hooks
  useEffect(() => {
    fetchLeagues()
  }, []);

  // Table Data
  const columns: Column<League>[] = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'leagueName',
          },
          {
            Header: 'Division',
            accessor: 'division',
          },
          {
            Header: 'Days',
            accessor: 'dates',
            Cell: ({ cell: { value } }) => (
              <span>{value.map(word => word[0]).join('')}</span>
            )
          },
          {
            Header: 'Times',
            accessor: 'times',
            Cell: ({ cell: { value } }) => (
              <span>{value.join(',')}</span>
            )
      },
      {
        Header: 'Teams',
        accessor: league => league.teams.length,
        id: 'teams',
      },   
      {
        Header: 'Actions',
        accessor: (league: League) => league,
        Cell: ({ cell: { value: league } }: { cell: { value: League } }) => (
          <div className="actions d-flex">
            <button className='btn btn-warning btn-sm me-2' onClick={() => handleShowUpdateModal(league._id)}>
              <FontAwesomeIcon icon={faEdit} /> Update
            </button>
            <button className='btn btn-warning btn-sm me-2' onClick={() => handleClickStatsModal(league._id)} disabled={!league.isActive}>
              <FontAwesomeIcon icon={faChartBar} /> Stats
            </button>
            {league.isActive ? (
              <>
                <button className='btn btn-warning btn-sm me-2' onClick={() => handleShowEndLeagueModal(league._id)}>
                  <FontAwesomeIcon icon={faFlagCheckered} /> End League
                </button>
              </>
            ) : (
              <button className='btn btn-success btn-sm me-2' onClick={() => handleShowFixturesModal(league._id)}>
                <FontAwesomeIcon icon={faPlay} /> Start League
              </button>
            )}
            <button className='btn btn-danger btn-sm' onClick={() => handleShowDeleteModal(league)} disabled={league.isActive}>
              <FontAwesomeIcon icon={faTrashAlt} /> Delete
            </button>
          </div>
        ),
      }
      
    ],
    [leagues]
  );
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({ columns, data: leagues });

  // Pagination
  const [meta, setMeta] = useState<Meta>({
    totalPages: 0,
    page: 1,
    pageSize: 25
  });
    
    // Modals
    const [selectedLeagueId, setSelectedLeagueId] = useState<string | null>(null);
  
    const closeModals = () => {
        setShowUpdateModal(false);
        setShowCreateModal(false);
        setShowFixtureModal(false)
        setShowStatsModal(false)
        setShowEndLeagueStatsModal(false)
        setLeagueToUpdate(null);
        setLeagueToCreate({ leagueName: "" });
        setSelectedLeagueId(null);
        setShowDeleteModal(false);
    };

    // Modal - Update
    const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
    const [leagueToUpdate, setLeagueToUpdate] = useState<League | null>(null);
    
    const handleShowUpdateModal = async (id: string) => {
        const fetchedLeague = await getLeague(id)
        if (fetchedLeague) {
            setLeagueToUpdate(fetchedLeague)
        }
      setSelectedLeagueId(id);
      setShowUpdateModal(true);
      fetchLeagues(1)
    };

    const handleUpdateInputChange = (event: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
    
      if (leagueToUpdate) {
        if (name === 'dates' || name === 'times') {
          // Making dates and times array from csv's
          setLeagueToUpdate({
            ...leagueToUpdate,
            [name]: value.split(',').map(item => item.trim()),
          });
        } else {
          setLeagueToUpdate({ ...leagueToUpdate, [name]: value });
        }
      }
    };
    
    const handleUpdateSubmit = async (e: any) => {
        e.preventDefault();
        if (leagueToUpdate) {
        await updateLeague(leagueToUpdate._id, leagueToUpdate);
        }
        closeModals();
        fetchLeagues(1)
    }
    
    // Modal - Create
    const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
    const [leagueToCreate, setLeagueToCreate] = useState<CreateLeagueDTO>({ leagueName: "" });
  
    const handleShowCreateModal = () => {
      setShowCreateModal(true);
      fetchLeagues(1)
    };

    const handleCreateInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        if (leagueToCreate) {
            setLeagueToCreate({ ...leagueToCreate, [name]: value });
        }
    };
    const handleCreateSubmit = async (e: any) => {
        e.preventDefault();
        if (leagueToCreate) {
            await createLeague(leagueToCreate);
        }
        closeModals();
        fetchLeagues(1)
    }
  
  // Modal - Fixtures
  const [showFixtureModal, setShowFixtureModal] = useState<boolean>(false);

  const handleShowFixturesModal = (id: string) => {
    setSelectedLeagueId(id);
    setShowFixtureModal(true);
  };

  // Modal - Stats
  const [showStatsModal, setShowStatsModal] = useState<boolean>(false);

  const handleClickStatsModal = (id: string) => {
    setSelectedLeagueId(id);
    setShowStatsModal(true);
  }

  const handleClickUpdateLeagueStats = async () => {
    if (selectedLeagueId) {
      await updateLeagueStats(selectedLeagueId);
    }
    closeModals();
    fetchLeagues(1)
    setSelectedLeagueId(null);
  }

  // Modal - End League
  const [showEndLeagueStatsModal, setShowEndLeagueStatsModal] = useState<boolean>(false);

  const handleShowEndLeagueModal = async (id: string) => {
    setSelectedLeagueId(id);
    setShowEndLeagueStatsModal(true);
  }

  const handleClickEndLeague = async () => {
    if (selectedLeagueId) {
      await endLeague(selectedLeagueId);
    }
    closeModals();
    fetchLeagues(1)
    setSelectedLeagueId(null);
  }

  // Modal - Delete
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [leagueToDelete, setLeagueToDelete] = useState<League | null>(null);

  const handleShowDeleteModal = (league: League) => {
    setLeagueToDelete(league);
    setShowDeleteModal(true);
  }

  const handleClickDelete = async () => {
    if (leagueToDelete?._id) {
      await deleteLeague(leagueToDelete._id)
    }
    closeModals();
    fetchLeagues(1)
    setLeagueToDelete(null)
  };

  return (
      <>
      <div className='d-flex flex-column mb-3'>
        {/* Fetch Data Button */}
        {/* <button 
            className='btn btn-bark mb-2' 
            onClick={() => fetchLeagues()}
            style={{ color: leagues.length > 0 ? '' : 'white' }}
          >
            Fetch Data
        </button> */}
        <StyledInstructions>
          {instructions.table.league}
        </StyledInstructions>
          <div className='ms-auto my-2 mr-3'>
          <button className='btn btn-primary' onClick={handleShowCreateModal}>Create League</button>
        </div>
        <div className='table-responsive'>
          <StyledTable {...getTableProps()}>
            <thead>
              {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                    <th {...column.getHeaderProps()}>{column.render('Header')}</th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.map(row => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map(cell => {
                      return (
                        <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </StyledTable>
        </div>
      </div>
      <div className='d-flex justify-content-end'>
        {leagues.length ?
            <StyledPagination>
              <ReactPaginate
                previousLabel={"Previous"}
                nextLabel={"Next"}
                pageCount={meta.totalPages}
                onPageChange={({ selected }) => {
                    fetchLeagues(selected + 1);
                  }}
                containerClassName={"pagination"}
                pageClassName={"page-item"}
                pageLinkClassName={"page-link"}
                previousClassName={"page-item"}
                nextClassName={"page-item"}
                previousLinkClassName={"page-link"}
                nextLinkClassName={"page-link"}
                activeClassName={"active"}
              />
            </StyledPagination>
        : null}
          </div>
          
      {/* Update Modal */}
      <ReactModal 
  modalOpen={showUpdateModal} 
  closeModal={() => closeModals()}
  component={
    <>
      <form onSubmit={handleUpdateSubmit} className='m-5'>
        <div className="mb-3">
          <strong>League Information:</strong><br/>
          League Name: {leagueToUpdate?.leagueName}<br/>
          Continent: {leagueToUpdate?.continent}<br/>
          Division: {leagueToUpdate?.division}<br/>
          Console: {leagueToUpdate?.console}<br/>
          Status: {leagueToUpdate?.status}<br/>
        </div>

        <div className="mb-3">
            <label htmlFor="leagueName" className="form-label">League Name</label>
            <input 
                type="text" 
                id="leagueName" 
                name="leagueName" 
                className="form-control" 
                value={leagueToUpdate?.leagueName || ""} 
                onChange={handleUpdateInputChange} 
            />
            </div>

            <div className="mb-3">
            <label htmlFor="continent" className="form-label">Continent</label>
            <input 
                type="text" 
                id="continent" 
                name="continent" 
                className="form-control" 
                value={leagueToUpdate?.continent || ""} 
                onChange={handleUpdateInputChange} 
            />
            </div>

            <div className="mb-3">
            <label htmlFor="division" className="form-label">Division</label>
            <input 
                id="division" 
                name="division" 
                className="form-control" 
                value={leagueToUpdate?.division || ""} 
                onChange={handleUpdateInputChange} 
            />
            </div>

            <div className="mb-3">
            <label htmlFor="console" className="form-label">Console</label>
            <input 
                type="text" 
                id="console" 
                name="console" 
                className="form-control" 
                value={leagueToUpdate?.console || ""} 
                onChange={handleUpdateInputChange} 
            />
            </div>

            <div className="mb-3">
            <label htmlFor="startDate" className="form-label">Start Date</label>
            <input 
                id="startDate" 
                name="startDate" 
                className="form-control" 
                value={leagueToUpdate?.startDate || ""} 
                onChange={handleUpdateInputChange} 
            />
              </div>
              <div className="mb-3">
            <label htmlFor="rounds" className="form-label">Rounds</label>
            <input 
                id="rounds" 
                name="rounds" 
                className="form-control" 
                value={leagueToUpdate?.rounds} 
                onChange={handleUpdateInputChange} 
            />
            </div>

            <div className="mb-3">
              <label htmlFor="dates" className="form-label">Days</label>
              <input 
                type="text" 
                id="dates" 
                name="dates" 
                className="form-control" 
                value={leagueToUpdate?.dates.join(', ') || ""} 
                placeholder="Enter dates separated by commas"
                onChange={handleUpdateInputChange} 
              />
            </div>

            <div className="mb-3">
              <label htmlFor="times" className="form-label">Times</label>
              <input 
                type="text" 
                id="times" 
                name="times" 
                className="form-control" 
                value={leagueToUpdate?.times.join(', ') || ""} 
                placeholder="Enter times separated by commas"
                onChange={handleUpdateInputChange} 
              />
            </div>


        <button type="submit" className="btn btn-primary">Update</button>
      </form>
    </>
  }
  />
          
    {/* Create Modal */}
    <ReactModal 
    modalOpen={showCreateModal} 
    closeModal={() => closeModals()}
    component={
        <>
        <form onSubmit={handleCreateSubmit} className='m-5'>
            <div className="mb-3">
            <label htmlFor="leagueName" className="form-label">League Name</label>
            <input 
                type="text" 
                id="leagueName" 
                name="leagueName" 
                className="form-control" 
                value={leagueToCreate?.leagueName || ""} 
                onChange={handleCreateInputChange} 
            />
            </div>
            <button type="submit" className="btn btn-primary">Create</button>
        </form>
        </>
    }
      />
      
      {/* Fixture Modal */}
      {selectedLeagueId ?
        <ReactModal 
          modalOpen={showFixtureModal} 
          closeModal={() => closeModals()}
          component={<StartLeagueModal leagueId={selectedLeagueId} refresh={() => { fetchLeagues(1); closeModals();}} />}
        />
        : null}
      
      {/* Stats Modal */}
      <ReactModal
        modalOpen={showStatsModal}
        component={<ConfirmationModalContent
          onConfirm={handleClickUpdateLeagueStats}
          onCancel={() => closeModals()}
          message="Are you sure you want to recalculate the league stats?  This action will update the team hub stats for this league."
        />}
        closeModal={() => closeModals()}
      />

      {/* End League Modal */}
      <ReactModal
        modalOpen={showEndLeagueStatsModal}
        component={<ConfirmationModalContent
          onConfirm={handleClickEndLeague}
          onCancel={() => closeModals()}
          message="Are you sure you want to end this league?  Ending a league will assign user stats to the leaderboard and delete all user stats for this league.  The Team Hub stats will remain unchanged, just don't add or remove any teams until you are starting the next league!"
        />}
        closeModal={() => closeModals()}
      />

      {/* Delete Modal */}
      <ReactModal
        modalOpen={showDeleteModal}
        component={<ConfirmationModalContent
          onConfirm={() => handleClickDelete()}
          onCancel={() => closeModals()}
          message={`Are you sure you want to delete the league ${leagueToDelete?.leagueName}?`}
        />}
        closeModal={() => closeModals()}
      />

    </>
  );
};

export default LeagueTable;
